import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColorMoment } from '@/hooks/useColorMoments';
import { hexToHsl } from '@/lib/utils';

function seededRandom(seed: number): number {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Generate an inky, blobby SVG path — same technique as WavyTransition.
 * 72 segments, 4 layered sine waves, Catmull-Rom to Bezier.
 */
function generateInkPath(
  progress: number,
  seed: number,
  originX: number,
  originY: number,
  radius: number,
): string {
  const baseRadius = radius * progress;
  if (baseRadius < 1) return '';

  const points: [number, number][] = [];
  const segments = 72;

  for (let i = 0; i < segments; i++) {
    const angle = (i / segments) * Math.PI * 2;

    const noise1 = Math.sin(angle * 3 + seed) * 0.15;
    const noise2 = Math.sin(angle * 7 + seed * 2.3) * 0.08;
    const noise3 = Math.sin(angle * 11 + seed * 1.7) * 0.05;
    const noise4 = Math.sin(angle * 5 + progress * Math.PI * 4) * 0.1 * progress;

    const noiseTotal = 1 + noise1 + noise2 + noise3 + noise4;
    const r = baseRadius * noiseTotal;

    points.push([originX + r * Math.cos(angle), originY + r * Math.sin(angle)]);
  }

  let path = `M ${points[0][0].toFixed(1)} ${points[0][1].toFixed(1)}`;

  for (let i = 0; i < points.length; i++) {
    const p0 = points[(i - 1 + points.length) % points.length];
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    const p3 = points[(i + 2) % points.length];

    const tension = 0.3;
    const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension;

    path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }

  return path + ' Z';
}

interface PaletteConstellationProps {
  moments: ColorMoment[];
  className?: string;
}

export function PaletteConstellation({ moments, className = '' }: PaletteConstellationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const animRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  // All visible blobs (settled + expanding)
  const [blobs, setBlobs] = useState<{ path: string; color: string }[]>([]);

  // Collect unique colors sorted by hue
  const colors = useMemo(() => {
    const seen = new Set<string>();
    const all: { hex: string; hue: number }[] = [];

    moments.forEach((m) => {
      m.colors.forEach((hex) => {
        const key = hex.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        const [h] = hexToHsl(hex);
        all.push({ hex, hue: h });
      });
    });

    all.sort((a, b) => a.hue - b.hue);
    return all.map((c) => c.hex);
  }, [moments]);

  // Infinite shuffled color sequence — non-repeating, reshuffles when exhausted
  const colorSeqRef = useRef<string[]>([]);
  const getColor = useCallback((index: number): string => {
    const seq = colorSeqRef.current;
    while (index >= seq.length) {
      const shuffled = [...colors];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(seededRandom(i + seq.length * 13.7) * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      if (seq.length > 0 && shuffled[0] === seq[seq.length - 1] && shuffled.length > 1) {
        [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
      }
      seq.push(...shuffled);
    }
    return seq[index];
  }, [colors]);

  // The viewBox size for our SVG
  const viewSize = 400;
  const cx = viewSize / 2;
  const cy = viewSize / 2;
  // Radius to fill the box corner-to-corner
  const maxRadius = Math.sqrt(cx * cx + cy * cy) * 1.1;

  // How long each blob takes to fully expand — slow, meditative
  const expandDuration = 24;
  // How often a new blob starts — tight stagger = many in flight at once (~16 visible)
  const stagger = 1.5;

  const animate = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = (timestamp - startTimeRef.current) / 1000;

    if (colors.length === 0) return;

    // Figure out which blobs are currently visible.
    // A new blob starts every `stagger` seconds. Each lives for `expandDuration`.
    // We only need to render blobs that haven't fully settled off-screen yet.
    // Keep ~6 recent ones (the settled ones as background, expanding ones on top).

    const currentBlobIndex = Math.floor(elapsed / stagger);
    const visibleCount = Math.ceil(expandDuration / stagger) + 1;
    const firstVisible = Math.max(0, currentBlobIndex - visibleCount);

    const result: { path: string; color: string }[] = [];

    for (let i = firstVisible; i <= currentBlobIndex; i++) {
      const blobStart = i * stagger;
      const age = elapsed - blobStart;
      if (age < 0) continue;

      const progress = Math.min(age / expandDuration, 1);

      // Cubic ease-out for smooth expansion
      const eased = 1 - Math.pow(1 - progress, 3);

      const color = getColor(i);
      const seed = i * 3.7 + 0.5; // unique seed per blob instance
      const path = generateInkPath(eased, seed, cx, cy, maxRadius);

      if (path) {
        result.push({ path, color });
      }
    }

    setBlobs(result);

    animRef.current = requestAnimationFrame(animate);
  }, [colors, getColor, expandDuration, stagger, cx, cy, maxRadius]);

  // Intersection observer
  useEffect(() => {
    if (moments.length === 0) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [moments.length]);

  // Start animation
  useEffect(() => {
    if (!isVisible || colors.length === 0) return;

    startTimeRef.current = 0;
    animRef.current = requestAnimationFrame(animate);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isVisible, animate, colors.length]);

  if (colors.length === 0) return null;

  const totalColors = moments.reduce((sum, m) => sum + m.colors.length, 0);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="flex items-center justify-between mb-3 px-2">
        <h3 className="text-sm font-medium text-muted-foreground tracking-wide">
          color fingerprint
        </h3>
        <span className="text-xs text-muted-foreground/60">
          {totalColors} colors &middot; {moments.length} moment{moments.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '2 / 1' }}>
        <svg
          viewBox={`0 0 ${viewSize} ${viewSize}`}
          preserveAspectRatio="xMidYMid slice"
          className={`w-full h-full transition-opacity duration-1000 ease-out ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Stacked ink splats — oldest at bottom, newest expanding on top */}
          {blobs.map((blob, i) => (
            <path key={i} d={blob.path} fill={blob.color} />
          ))}
        </svg>
      </div>
    </div>
  );
}
